import { logger } from '@adornis/base/logging.js';
import type { ValueEvent } from '@adornis/base/utilTypes.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-link.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import '@adornis/file-utils/client/x-file-input.js';
import '@adornis/fonts/fonts.js';
import '@adornis/forms/x-checkbox.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import '@adornis/forms/x-input.js';
import type { XInput } from '@adornis/forms/x-input.js';
import '@adornis/internationalization/client/language-picker/x-country-picker.js';
import '@adornis/internationalization/client/x-tel-input.js';
import '@adornis/popover/x-dropdown-multiselection.js';
import '@adornis/popover/x-dropdown-selection.js';
import { Language } from '@adornis/translation-core/languages.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { login } from '@adornis/users/client/loginFunctions.js';
import { validatePassword } from '@adornis/users/db/validators.js';
import { getCompanyInfo } from '@ils/config/project-config.js';
import { ExamType, FederalStatesGermany, Gender } from '@ils/static/enums.js';
import { html, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { choose } from 'lit/directives/choose.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import {
  EventLogSignupProfileDataConfirm,
  EventLogSignupRegisterUser,
  EventLogSignupSetMailAndPasswordCancel,
  EventLogSignupSetMailAndPasswordConfirm,
} from '../../db/eventlog.js';
import { ILSProfile, ILSUser } from '../../db/ils-user.js';
import './ils-login.js';

/**
 * @element ils-signup
 */
@customElement('ils-signup')
export class ILSSignup extends ChemistryLitElement {
  override styles() {
    return [...super.styles(), { ':host': { display: 'flex', height: '100%', width: '100%' } }];
  }

  @property({ type: String, attribute: 'exam-type' }) private readonly examType = ExamType.FAMED;
  @state() private _step: 0 | 1 = 0;
  @state() private _showLogin = false;

  @state() public errorMsg: { key?: string; text?: string } = {};
  @state() private readonly _form = new FormController(
    this,
    new ILSUser({
      username: 'EMPTY',
    }),
    {
      onSuccess: user => {
        return this._register(user);
      },
      error: err => {
        logger.error({ err }, 'Fehler beim Registrieren');
        void XDialog.alert(
          'Registrierung konnte nicht abgeschlossen werden. Bitte füllen Sie alle Felder korrekt aus!',
        );
      },
    },
  );

  private _email?: string;
  private _emailRepeat?: string;

  private _password?: string;
  private _passwordRepeat?: string;
  private _passwordsMatch = false;

  override willUpdate(changedProperties: PropertyValues) {
    super.willUpdate(changedProperties);

    if (!this._form.document) return;
    if (!this._form.document.profile) this._form.document.profile = new ILSProfile({});
  }

  override render() {
    if (this._showLogin) return this._loginDisplay();
    return html`
      <x-flex space="lg" ${css({ flex: '1', minWidth: '0' })}>
        ${this._steps()} ${choose(this._step, [[1, () => this._profileData()]], () => this._mailAndPassword())}
      </x-flex>
    `;
  }

  private _mailAndPassword() {
    return html`
      <x-flex space="md">
        <x-flex space="sm">
          <x-h2> E-Mail und Passwort </x-h2>
          <x-text tone="subtle"> Geben Sie Ihre E-Mail und Ihr Passwort ein! </x-text>
        </x-flex>
        <x-flex space="sm" horizontal wrap crossaxis-end>
          <x-input
            flex
            ${css({ minWidth: '300px' })}
            ${this._form.field('email')}
            placeholder="E-Mail"
            type="email"
            @value-picked=${(e: ValueEvent<string>) => {
              this._email = e.detail.value;
              this._validateEmailRepeat();
              if (this._form.document) this._form.document.username = this._email;
            }}
          ></x-input>
          <x-input
            flex
            ${css({ minWidth: '300px' })}
            id="email-repeat"
            placeholder="E-Mail Wiederholen"
            type="email"
            @value-picked=${(e: ValueEvent<string>) => {
              this._emailRepeat = e.detail.value;
              this._validateEmailRepeat();
            }}
            .value=${this._emailRepeat}
          ></x-input>
        </x-flex>
        <x-flex space="sm" horizontal wrap crossaxis-end>
          <x-input
            flex
            ${css({ minWidth: '300px' })}
            type="password"
            placeholder="Passwort"
            @value-changed="${(e: CustomEvent) => {
              if (e.detail.value === this._password) return;
              this._password = e.detail.value;
              this._passwordsMatch = this._passwordRepeat === this._password;
              this.requestUpdate();
            }}"
            .value=${this._password}
            error="${ifDefined(
              this._password && !this._validatePassword(this._password)
                ? 'Das Passwort muss mindestens 8 Zeichen lang sein, einen Großbuchstaben, eine Zahl und ein Sonderzeichen beinhalten.'
                : undefined,
            )}"
          ></x-input>
          <x-input
            flex
            ${css({ minWidth: '300px' })}
            type="password"
            passwordrepeat
            placeholder="Passwort Wiederholung"
            @value-changed="${(e: CustomEvent) => {
              this._passwordRepeat = e.detail.value;
              this._passwordsMatch = e.detail.value === this._password;
              this.requestUpdate();
            }}"
            .value=${this._passwordRepeat}
            error="${ifDefined(
              this._passwordRepeat && !this._passwordsMatch ? 'Das Passwort stimmt nicht überein.' : undefined,
            )}"
          ></x-input>
        </x-flex>
        <x-flex horizontal space-between crossaxis-center wrap space="lg">
          ${this._gotAnAccount()}
          <x-flex horizontal space="md">
            <x-button
              mode="outline"
              @click=${async () => {
                const confirm = await XDialog.confirm(
                  'Möchtest du die Anmeldung abbrechen? Alle eingegebenen Daten gehen dabei verloren!',
                );
                if (confirm) {
                  this.dispatchEvent(new CustomEvent('cancel'));
                  void new EventLogSignupSetMailAndPasswordCancel({}).create();
                }
              }}
            >
              Abbrechen
            </x-button>
            <x-button
              @click=${async () => {
                if (
                  this._email &&
                  this._emailRepeat &&
                  this._password &&
                  this._passwordRepeat &&
                  this._validateEmailRepeat() &&
                  this._passwordsMatch &&
                  this._validatePassword(this._password)
                ) {
                  if (await ILSUser.getEmailExists(this._email)())
                    return XDialog.alert(
                      'Diese E-Mail ist bereits registriert. Bitte melden Sie sich an über "Ich habe bereits einen Account"',
                    );
                  this._step = 1;
                  void new EventLogSignupSetMailAndPasswordConfirm({}).create();
                } else void XDialog.alert('Bitte füllen Sie erst alle Daten korrekt aus bevor Sie weiter gehen!');
              }}
            >
              Weiter
            </x-button>
          </x-flex>
        </x-flex>
      </x-flex>
    `;
  }

  private agbLink() {
    const brandedAGB = getCompanyInfo(this.examType ?? ExamType.FAMED).agbUrl;

    return html`
      <x-link href=${brandedAGB} ${css({ color: this.colors.accent })} @click=${e => e.stopPropagation()}>
        AGB's
      </x-link>
    `;
  }

  private gpdrLink() {
    return html`
      <x-link
        href=${getCompanyInfo(this.examType ?? ExamType.FAMED).dataPrivacyUrl}
        ${css({ color: this.colors.accent })}
        @click=${e => e.stopPropagation()}
      >
        Datenschutzrichtlinien
      </x-link>
    `;
  }

  private _profileData() {
    return html`
      <x-flex space="lg">
        <x-flex space="md">
          <x-flex space="sm">
            <x-h2> Persönliche Daten </x-h2>
            <x-text tone="subtle"> Geben Sie Ihre persönlichen Daten ein! </x-text>
          </x-flex>
          <x-flex space="sm">
            <x-dropdown-selection
              ${this._form.field('profile', 'gender')}
              placeholder="Geschlecht"
              .selectables=${Object.values(Gender)}
            ></x-dropdown-selection>
            <x-input ${this._form.field('ecommerce', 'firstName')} placeholder="Vorname"></x-input>
            <x-input ${this._form.field('ecommerce', 'lastName')} placeholder="Nachname"></x-input>
            <x-input
              show-label
              type="date"
              ${this._form.field('profile', 'dateOfBirth')}
              placeholder="Geburtsdatum"
            ></x-input>
            <x-input ${this._form.field('profile', 'birthplace')} placeholder="Geburtsort"></x-input>
            <x-country-picker
              ${this._form.field('profile', 'birthCountry')}
              placeholder="Geburtsland"
              .language=${Language.GERMAN}
              empty-state="Geben Sie den Namen Ihres Geburtslandes ein"
            ></x-country-picker>
            <x-input ${this._form.field('ecommerce', 'street')} placeholder="Adresse"></x-input>
            <x-flex horizontal space="md" crossaxis-end>
              <x-input
                ${this._form.field('ecommerce', 'zip')}
                placeholder="PLZ"
                ${css({ maxWidth: '30%' })}
                type="zip"
              ></x-input>
              <x-input flex ${this._form.field('ecommerce', 'city')} placeholder="Stadt"></x-input>
            </x-flex>
            <x-country-picker
              ${this._form.field('ecommerce', 'country')}
              placeholder="Land"
              .language=${Language.GERMAN}
              empty-state="Geben Sie den Namen Ihres Landes ein"
            ></x-country-picker>
            <x-tel-input
              searchable
              ${this._form.field('ecommerce', 'phone')}
              placeholder="Telefonnummer"
              type="tel"
            ></x-tel-input>
            <x-flex horizontal space="xs" crossaxis-center>
              <x-dropdown-selection
                flex
                placeholder="Bundesland Approbationsantrag"
                .selectables=${Object.values(FederalStatesGermany)}
                ${this._form.field('profile', 'targetFederalState')}
                @value-picked=${() => this.requestUpdate()}
              >
              </x-dropdown-selection>
              <x-icon outlined ${css({ fontSize: '25px', color: this.colors.tone.subtle })}>
                help <x-tooltip> In welchem Bundesland wollen Sie ihren Approbationsantrag stellen? </x-tooltip>
              </x-icon>
            </x-flex>
          </x-flex>
        </x-flex>
        ${this._form.document?.profile?.targetFederalState &&
        this._form.document.profile.targetFederalState !== FederalStatesGermany.BAYERN
          ? html`
              <x-flex space="md">
                <x-flex space="sm">
                  <x-h2> B2-Zertifikat </x-h2>
                  <x-text tone="subtle">
                    Laden Sie hier eine Kopie Ihres B2-Zertifikats hoch. Das Zertifikat muss am Tag der Prüfung im
                    Original vorgelegt werden.
                  </x-text>
                </x-flex>
                <x-file-input
                  ${this._form.field('profile', 'b2Certificate')}
                  placeholder="B2 Zertifikat"
                ></x-file-input>
              </x-flex>
            `
          : nothing}
        <x-flex space="lg">
          <x-flex space="md">
            <x-flex space="sm">
              <x-h2> Datenschutzerklärung & AGB </x-h2>
              <x-text tone="subtle">
                Bitte stimmen Sie der Datenschutzerklärung und den allgemeinen Geschäftsbedingungen zu!
              </x-text>
            </x-flex>
            <x-flex space="sm">
              <x-checkbox
                flex
                ${this._form.field('ecommerce', 'acceptedLegal')}
                .label=${html` Ich erkläre mich mit unseren ${this.agbLink()}, sowie den ${this.gpdrLink()}
                einverstanden. Nähere Informationen zur Verarbeitung Ihrer Daten entnehmen Sie bitte unserer
                Datenschutzerklärung.`}
                @value-changed=${(e: ValueEvent<boolean>) => {
                  if (e.detail.value) this.errorMsg = {};
                }}
              ></x-checkbox>
            </x-flex>
          </x-flex>
          <x-flex space="md">
            <x-h2> Newsletter </x-h2>
            <x-flex space="sm">
              <x-checkbox
                flex
                ${this._form.field('profile', 'wantsNewsletter')}
                .label=${html`Ja, ich möchte den FaMed-Newsletter erhalten.`}
              ></x-checkbox>
            </x-flex>
          </x-flex>
        </x-flex>
        ${this.errorMsg.key === 'global'
          ? html` <x-text ${css({ color: this.colors.tone.error })}> ${this.errorMsg.text} </x-text> `
          : nothing}
        <x-flex horizontal end crossaxis-center wrap space="lg">
          <x-flex horizontal space="md">
            <x-button mode="outline" @click=${() => (this._step = 0)}> Zurück </x-button>
            <x-button
              @click=${(e: MouseEvent) => {
                void this._form.validate({ mouseEvent: e });
                void new EventLogSignupProfileDataConfirm({}).create();
              }}
            >
              Account erstellen &amp; Weiter
            </x-button>
          </x-flex>
        </x-flex>
      </x-flex>
    `;
  }

  private _steps() {
    const label = (content: Renderable, isLeft?: boolean, isActive = false) => html`
      <x-flex
        center
        crossaxis-center
        padding="xs"
        ${css({
          ...(isLeft
            ? {
                borderTopLeftRadius: this.sizes.borderRadiusSecondary,
                borderBottomLeftRadius: this.sizes.borderRadiusSecondary,
              }
            : {
                borderTopRightRadius: this.sizes.borderRadiusSecondary,
                borderBottomRightRadius: this.sizes.borderRadiusSecondary,
              }),
          background: isActive ? this.colors.accent : this.colors.neutral.shade(200),
          color: this.colors.white,
        })}
      >
        ${content}
      </x-flex>
    `;

    const num = (num: number, isActive = false) =>
      label(
        html`
          <x-flex
            center
            crossaxis-center
            ${css({
              aspectRatio: '1 / 1',
              height: '100%',
              color: this.colors.white,
              borderRadius: '50%',
            })}
          >
            <x-text bold> ${num} </x-text>
          </x-flex>
        `,
        true,
        isActive,
      );

    const line = (step: number, text: string, isActive = false) => html`
      <x-flex horizontal ${css({ gap: '2px' })}>
        ${num(step, isActive)} ${label(html` <x-text bold> ${text} </x-text> `, false, isActive)}
      </x-flex>
    `;

    return html`
      <x-flex horizontal space="md" crossaxis-center center wrap>
        ${line(1, 'Email und Passwort', this._step >= 0)} ${line(2, 'Persönliche Angaben', this._step >= 1)}
      </x-flex>
    `;
  }

  private _gotAnAccount() {
    return html`
      <x-button mode="text" icon="account_circle" @click=${() => (this._showLogin = true)}>
        Ich habe bereits einen Account
      </x-button>
    `;
  }

  private _loginDisplay() {
    return html`
      <ils-login
        ${css({ flex: '1' })}
        no-reroute-on-login
        show-no-account
        exam-type=${this.examType}
        @no-account=${() => (this._showLogin = false)}
      ></ils-login>
    `;
  }

  private async _register(user: ILSUser) {
    if (!this._password || !user.profile) throw new Error('No password or profile provided');
    const userID = await ILSUser.registerILSUser(
      user.email,
      this._password,
      user.ecommerce,
      user.profile.toObject() as PartialEntityData<ILSProfile>,
      this.examType,
    )().catch((error: Error) => {
      if (error.message.includes('Email is already in use')) {
        this.errorMsg = { key: 'global', text: 'Diese E-Mail Adresse ist bereits registriert' };
      } else if (error.message.includes('Password')) {
        this.errorMsg = {
          key: 'global',
          text: 'Registrierungsfehler: Das Passwort muss mindestens 8 Zeichen lang sein, einen Großbuchstaben, eine Zahl und ein Sonderzeichen beinhalten.',
        };
      } else {
        logger.error({ error }, 'Error handling in ils-signup');
        this.errorMsg = {
          key: 'global',
          text: error.message,
        };
      }
      logger.error(error, 'error while registering user');
      void XSnackbar.show('Es ist ein Fehler bei der Anmeldung aufgetreten.');
    });
    if (!userID) return;
    void new EventLogSignupRegisterUser({ registeredUserID: userID }).create();
    // * we need this password format because of the otp middleware
    await login(user.email, JSON.stringify({ password: this._password, otp: '' }))
      .then(() => currentUser().isReady)
      .catch((error: Error) => {
        this.errorMsg = {
          key: 'global',
          text: 'Login Error: ' + error.message,
        };
        logger.error(error, 'error while logging in');
        void XSnackbar.show('Beim Einloggen ist etwas schief gelaufen.');
      });
  }

  private _validateEmailRepeat() {
    if (!this._isRepeatedMailValid(this._email, this._emailRepeat)) {
      this._setErrorForEmailRepeat();
      return false;
    } else {
      this._setErrorForEmailRepeat(true);
      return true;
    }
  }

  private _isRepeatedMailValid(email?: string, emailRepeat?: string) {
    return email === emailRepeat;
  }

  private _setErrorForEmailRepeat(clear = false) {
    const emailRepeatField = this.renderRoot.querySelector<XInput>('#email-repeat');
    if (!emailRepeatField) return;
    emailRepeatField.error = clear ? undefined : 'E-Mail-Adressen müssen übereinstimmen';
  }

  private _validatePassword(password: string) {
    try {
      validatePassword({ value: password });
      return true;
    } catch {
      return false;
    }
  }
}
